if (!customElements.get("variant-picker")) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
    }

    get hideUnavailableOptions() {
      return (
        this.closest("[data-variant-picker]")?.dataset.hideUnavailableOptions ===
        "true"
      );
    }

    get selectedOptionValues() {
      const options = this.querySelectorAll(
        "select, .variant-picker__field input[type='radio']"
      );
      const selectedValues = new Map();

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (field.tagName.toLowerCase() === "select") {
          const selectedOption = Array.from(field.options).find(
            (opt) => opt.selected && !opt.disabled
          );
          if (selectedOption) {
            selectedValues.set(fieldName, selectedOption.dataset.optionValueId);
          }
        } else if (
          field.tagName.toLowerCase() === "input" &&
          field.type === "radio"
        ) {
          const checkedInput = field.checked ? field : null;
          if (checkedInput) {
            selectedValues.set(fieldName, checkedInput.dataset.optionValueId);
          }
        }
      });

      if (this.hideUnavailableOptions) {
        const result = [];
        options.forEach((field) => {
          const fieldName = field.name || field.dataset.fieldName;
          if (selectedValues.has(fieldName)) {
            const optionValueId = selectedValues.get(fieldName);
            if (optionValueId && !result.includes(optionValueId)) {
              result.push(optionValueId);
            }
          }
        });
        return result;
      }

      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;

        if (!selectedValues.has(fieldName)) {
          if (field.tagName.toLowerCase() === "select") {
            const firstValidOption = Array.from(field.options).find(
              (opt) => !opt.disabled
            );
            if (firstValidOption) {
              selectedValues.set(
                fieldName,
                firstValidOption.dataset.optionValueId
              );
            }
          } else if (
            field.tagName.toLowerCase() === "input" &&
            field.type === "radio"
          ) {
            const firstInput = field;
            if (firstInput) {
              selectedValues.set(fieldName, firstInput.dataset.optionValueId);
            }
          }
        }
      });

      const result = [];
      options.forEach((field) => {
        const fieldName = field.name || field.dataset.fieldName;
        if (selectedValues.has(fieldName)) {
          const optionValueId = selectedValues.get(fieldName);
          if (optionValueId && !result.includes(optionValueId)) {
            result.push(optionValueId);
          }
        }
      });
      return result;
    }

    getInputForEventTarget(target) {
      return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
    }

    getOptionPosition(target) {
      const field = target?.closest?.("[data-picker-field]");
      const rawPosition =
        target?.dataset?.optionPosition ||
        field?.dataset?.optionPosition ||
        target?.name?.match(/-(\d+)$/)?.[1];
      const position = Number(rawPosition);
      return Number.isFinite(position) ? position : null;
    }

    clearFollowingOptions(optionPosition) {
      const position = Number(optionPosition);
      if (!Number.isFinite(position)) return;

      this.querySelectorAll("[data-picker-field][data-option-position]").forEach(
        (field) => {
          const fieldPosition = Number(field.dataset.optionPosition);
          if (!Number.isFinite(fieldPosition) || fieldPosition <= position) return;

          const select = field.querySelector("select");
          if (select) {
            select.selectedIndex = -1;
            select.querySelectorAll("option").forEach((option) => {
              option.removeAttribute("selected");
            });
          }

          field.querySelectorAll("input[type='radio']").forEach((input) => {
            input.checked = false;
            input.removeAttribute("checked");

            const option = input.closest(".variant-picker__option");
            if (
              this.hideUnavailableOptions &&
              option?.classList.contains("variant-picker__option--soldout")
            ) {
              option.hidden = true;
              option.classList.add("variant-picker__option--hidden");
            }
          });

          field.dataset.selectedValue = "";
          const selectedValue = field.querySelector(".selected-value");
          if (selectedValue) selectedValue.textContent = "";
        }
      );
    }

    hasFollowingOptions(optionPosition) {
      const position = Number(optionPosition);
      if (!Number.isFinite(position)) return false;

      return Array.from(
        this.querySelectorAll("[data-picker-field][data-option-position]")
      ).some((field) => {
        const fieldPosition = Number(field.dataset.optionPosition);
        return Number.isFinite(fieldPosition) && fieldPosition > position;
      });
    }

    selectFirstAvailableFollowingOptions(optionPosition) {
      const position = Number(optionPosition);
      if (!Number.isFinite(position)) return null;

      let changedControl = null;

      this.querySelectorAll("[data-picker-field][data-option-position]").forEach(
        (field) => {
          const fieldPosition = Number(field.dataset.optionPosition);
          if (!Number.isFinite(fieldPosition) || fieldPosition <= position) return;

          const select = field.querySelector("select");
          if (select) {
            const option = Array.from(select.options).find(
              (item) => !item.disabled && !item.hidden
            );

            if (option && !option.selected) {
              select.value = option.value;
              option.selected = true;
              changedControl = select;
            }

            const selectedValue = field.querySelector(".selected-value");
            if (selectedValue) selectedValue.textContent = option?.value || "";
            field.dataset.selectedValue = option?.value || "";
            return;
          }

          const input = Array.from(
            field.querySelectorAll("input[type='radio']")
          ).find((item) => {
            const option = item.closest(".variant-picker__option");
            return (
              !item.disabled &&
              !item.classList.contains("disabled") &&
              !option?.hidden
            );
          });

          if (input && !input.checked) {
            input.checked = true;
            changedControl = input;
          }

          const selectedValue = field.querySelector(".selected-value");
          if (selectedValue) selectedValue.textContent = input?.value || "";
          field.dataset.selectedValue = input?.value || "";
        }
      );

      return changedControl;
    }

    connectedCallback() {
      this.addEventListener("change", (event) => {
        const target = this.getInputForEventTarget(event.target);
        const changedOptionPosition = this.getOptionPosition(target);
        const shouldClearFollowingOptions =
          this.hideUnavailableOptions && changedOptionPosition;

        if (shouldClearFollowingOptions) {
          this.clearFollowingOptions(changedOptionPosition);
        }

        window.FoxThemeEvents.emit(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: this.selectedOptionValues,
            clearFollowingOptionPosition: shouldClearFollowingOptions
              ? changedOptionPosition
              : null,
          },
        });
      });
    }
  }

  customElements.define("variant-picker", VariantPicker);
}
